import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { BaseStorePricelist, VariantModelValue } from '../Constants/Configuration'
import { PriceType } from '../Constants/TableConfiguration'
import { localeSpecificDate } from './DateHelpers'
import { renderTranslation } from './GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'

const retrieveListingList = (priceData: any) => {
  return priceData?.map(x => x.name) ?? []
}

const getListingBySize = (priceData: any) => {
  return priceData?.find(x => x.isBySize)
}

const getListingVat = (priceData: any, listingName: string) => {
  return priceData?.find(x => x.name === listingName)?.ltVat
}
const getPricelistByName = (priceData: any, listingName: string) => {
  return priceData?.find(x => x.name === listingName)
}
const getPriceDataByType = (priceData: any, priceTypes: string[]) => {
  return priceData
    ? priceData.filter(x => priceTypes.indexOf(x.isSell ? PriceType.sell : PriceType.purchase) !== -1)
    : []
}

interface DateSizeRowData {
  fakeIndex: string
  startDate: string | null
  listings: string[]
  isCurrent: boolean
  size?: string
  ruleId: number
  ruleName: string
  endDate: string | null
  type: number
}

export interface Size {
  sizeName: string
  sizeId: number
  sizeOrder: number
}

export interface price {
  buyPrice: number
  sellPrice: number
  size: Size
}

export enum PriceTypeSave {
  Buy,
  Sel,
  Ref,
}

export interface basePricelist {
  name: string
  isSell: boolean
  b2c: boolean
  vat: number
  ltvat: boolean
  currencyDescription: string
  currencySymbol: string
  isVisibleInDiwa: boolean
  typeOfChannel: TypeOfChannel
  isBySize: boolean
  isBaseForSize: boolean
  prices: price[]
}
export interface PricetoSave {
  pricelist: string
  PriceType: PriceTypeSave
  Price: number
  SizeId: number
}

const getMainPriceTableData = (filterPriceData: any, isBySize: boolean) => {
  const results: DateSizeRowData[] = []

  filterPriceData.forEach(result => {
    result.prices.forEach(price => {
      const elementIndex = results.findIndex(x => x.startDate === price.startDate && !x.size)
      if (elementIndex === -1) {
        const dateRow: DateSizeRowData = {
          fakeIndex: price.startDate + '_noSize',
          startDate: price.startDate,
          endDate: price.endDate,
          ruleName: price.ruleName,
          isCurrent: price.isCurrent,
          listings: [],
          ruleId: price.ruleId,
          type: price.type,
        }

        results.push(dateRow)

        if (isBySize) {
          const sizeRow: DateSizeRowData = {
            fakeIndex: price.startDate + '_' + price.size,
            startDate: price.startDate,
            endDate: price.endDate,
            ruleName: price.ruleName,
            isCurrent: price.isCurrent,
            listings: [],
            size: price.size,
            ruleId: price.ruleId,
            type: price.type,
          }

          results.push(sizeRow)
        }
      } else {
        if (isBySize) {
          const sizeIndex = results.findIndex(x => x.startDate === price.startDate && x.size && x.size === price.size)
          if (sizeIndex === -1) {
            const sizeRow: DateSizeRowData = {
              fakeIndex: price.startDate + '_' + price.size,
              startDate: price.startDate,
              endDate: price.endDate,
              ruleName: price.ruleName,
              isCurrent: price.isCurrent,
              listings: [],
              size: price.size,
              ruleId: price.ruleId,
              type: price.type,
            }

            results.push(sizeRow)
          }
        }
      }
    })
  })

  results.forEach((result: DateSizeRowData) => {
    filterPriceData.forEach(priceData => {
      const listingMatchDate = priceData.prices.find(x => x.startDate === result.startDate)
      if (listingMatchDate)
        if (result.listings.indexOf(priceData.name) === -1) {
          result.listings.push(priceData.name)
        }
    })
  })

  return results.sort((a, b) => {
    return localeSpecificDate(a.startDate).isBefore(localeSpecificDate(b.startDate)) ? -1 : 1
  })
}

interface SellAndPurchasePriceRender {
  sellPrice: number
  buyPrice: number
}

const getPriceSellAndPurchase = (priceData: any, size?: string): SellAndPurchasePriceRender => {
  if (size) {
    const isBySizeResult = getListingBySize(priceData)

    if (isBySizeResult) {
      const sizeFilteredResult = isBySizeResult.prices.find(priceInfo => priceInfo.size === size)
      return {
        sellPrice: sizeFilteredResult?.sellPrice ?? 0,
        buyPrice: sizeFilteredResult?.buyPrice ?? 0,
      }
    }
  } else {
    const modelloVarianteResult = priceData?.find(x => x.name === VariantModelValue)

    if (modelloVarianteResult) {
      const currentPriceResult = modelloVarianteResult.prices.find(priceInfo => priceInfo.isCurrent)
      return {
        sellPrice: currentPriceResult?.sellPrice ?? 0,
        buyPrice: currentPriceResult?.buyPrice ?? 0,
      }
    }
  }

  return {
    sellPrice: 0,
    buyPrice: 0,
  }
}
const getPriceSellAndPurchaseForDate = (priceData: any, date: Date, size?: string): SellAndPurchasePriceRender => {
  if (!date) return getPriceSellAndPurchase(priceData, size)
  if (size) {
    const isBySizeResult = getListingBySize(priceData)

    if (isBySizeResult) {
      const sizeFilteredResultPrices = isBySizeResult.prices.filter(priceInfo => priceInfo.size === size)
      const sizeFilteredResult = sizeFilteredResultPrices?.find(element => element.startDate == date)

      return {
        sellPrice: sizeFilteredResult?.sellPrice ?? 0,
        buyPrice: sizeFilteredResult?.buyPrice ?? 0,
      }
    }
  } else {
    const modelloVarianteResult = priceData?.find(x => x.name === VariantModelValue)

    if (modelloVarianteResult) {
      const currentPriceResult = modelloVarianteResult.prices.find(element => element.startDate == date)
      return {
        sellPrice: currentPriceResult?.sellPrice ?? 0,
        buyPrice: currentPriceResult?.buyPrice ?? 0,
      }
    }
  }

  return {
    sellPrice: 0,
    buyPrice: 0,
  }
}
const hasDiscountStoresRows = (priceData: any): boolean => {
  const rowForListing = priceData
    .map(e => e.prices)
    .flat()
    .filter(e => e.storesDiscounts && e.storesDiscounts.length > 0)
  return rowForListing && rowForListing.length > 0
}

const getBasePriceForStore = (priceData: any, listing: string): number => {
  const listingPrices = priceData.filter(e => e.name === listing)[0]
  return listingPrices.prices && listingPrices.prices.length > 0
    ? listingPrices.isSell
      ? listingPrices.prices[0].sellPrice
      : listingPrices.prices[0].buyPrice
    : 0
}
const getRowInnerElementsCount = (priceData: any, date: Date): number => {
  const rowForListing = priceData
    .map(e => e.prices.filter(p => p.startDate == date))
    .flat()
    .sort((a, b) => {
      return (b.storesDiscounts ? b.storesDiscounts.length : 0) - (a.storesDiscounts ? a.storesDiscounts.length : 0)
    })

  return rowForListing && rowForListing[0] && rowForListing[0].storesDiscounts
    ? rowForListing[0].storesDiscounts.length > 1
      ? rowForListing[0].storesDiscounts.length
      : 1
    : 1
}
const getPricelistName = (name: string, language: string): string => {
  if (name === BaseStorePricelist) {
    return renderTranslation(language, TranslationKeys.pricelistBaseStore)
  }
  return name
}

export {
  retrieveListingList,
  getPriceDataByType,
  getListingBySize,
  getMainPriceTableData,
  getPriceSellAndPurchase,
  getListingVat,
  getPriceSellAndPurchaseForDate,
  getRowInnerElementsCount,
  hasDiscountStoresRows,
  getBasePriceForStore,
  getPricelistByName,
  getPricelistName,
}
